import { Controller } from "@hotwired/stimulus"
import { shouldActivateClickEvent } from "../../../../utils/keyboardAccessibility"

// Educator Home AI Assistant chat history + delete-confirm nested modals.
export default class extends Controller {
  static targets = ["historyModal", "confirmModal", "confirmTitle", "destroyForm"]

  static values = {
    activeThreadId: Number,
  }

  connect() {
    this.onEscape = this.onEscape.bind(this)
    // Capture so nested Esc closes history/confirm before the parent AI modal.
    window.addEventListener("keyup", this.onEscape, true)
  }

  disconnect() {
    window.removeEventListener("keyup", this.onEscape, true)
  }

  open(event) {
    event?.preventDefault?.()
    this.reloadHistoryFrame()
    this.historyRailsuiModal?.show?.(event)
  }

  close(event) {
    event?.preventDefault?.()
    this.closeConfirm(event)
    this.historyRailsuiModal?.close?.(event)
  }

  openConfirm(event) {
    if (!this.shouldActivate(event)) return

    event.preventDefault()
    event.stopPropagation()

    const title = event.params?.title || ""
    const url = event.params?.url || ""

    if (this.hasConfirmTitleTarget) {
      this.confirmTitleTarget.textContent = title
    }

    if (this.hasDestroyFormTarget && url) {
      this.destroyFormTarget.action = url
    }

    this.confirmRailsuiModal?.show?.(event)
  }

  closeConfirm(event) {
    event?.preventDefault?.()
    this.confirmRailsuiModal?.close?.(event)
  }

  afterSelect(event) {
    // Close history after navigating to a prior thread (turbo patch).
    requestAnimationFrame(() => this.close(event))
  }

  afterDestroy(event) {
    if (event.detail?.success === false) return
    this.closeConfirm(event)
  }

  shouldActivate(event) {
    return shouldActivateClickEvent(event)
  }

  onEscape(event) {
    if (event.key !== "Escape") return

    const confirmOpen = this.confirmModalOpen
    const historyOpen = this.historyModalOpen
    if (!confirmOpen && !historyOpen) return

    event.preventDefault()
    event.stopPropagation()

    if (confirmOpen) {
      this.closeConfirm(event)
      return
    }

    this.close(event)
  }

  reloadHistoryFrame() {
    const frame = this.historyModalTarget?.querySelector?.(
      "turbo-frame#educator-home-ai-assistant-history",
    )
    frame?.reload?.()
  }

  get historyModalOpen() {
    const container = this.historyModalTarget?.querySelector?.(
      "[data-railsui-modal-target='container']",
    )
    return Boolean(container && !container.classList.contains("hidden"))
  }

  get confirmModalOpen() {
    if (!this.hasConfirmModalTarget) return false
    const container = this.confirmModalTarget.querySelector(
      "[data-railsui-modal-target='container']",
    )
    return Boolean(container && !container.classList.contains("hidden"))
  }

  get historyRailsuiModal() {
    if (!this.hasHistoryModalTarget) return null
    return this.application.getControllerForElementAndIdentifier(
      this.historyModalTarget,
      "railsui-modal",
    )
  }

  get confirmRailsuiModal() {
    if (!this.hasConfirmModalTarget) return null
    return this.application.getControllerForElementAndIdentifier(
      this.confirmModalTarget,
      "railsui-modal",
    )
  }
}
